<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { add, differenceInCalendarDays } from 'date-fns'

import { formatCurrency, formatMonthYear } from '@/utils/format'
import { toPayNow } from '@/utils/debtStage'
import type { RespiteParams } from '@/stores/dialogsStore'
import type { OcrNumber } from '@/types/ocr-numbers'
import type { SettlementDocument } from '@/types/settlement-documents'

import mdiInformation from '~icons/mdi/information'
import mdiChevronLeft from '~icons/mdi/chevron-left'
import mdiChevronRight from '~icons/mdi/chevron-right'

// Date-picker window of the respite dialog, bounded to [schedule_to_execute + days.min, + days.max].
// The picked date is sent as calendar days from the baseline because the api adds them to it.
const props = defineProps<{
  document: SettlementDocument
  params: RespiteParams
  ocr?: OcrNumber
  loading?: boolean
}>()

const emit = defineEmits<{
  cancel: []
  confirm: [payload: { days: number; date: Date }]
}>()

const { t } = useI18n()

// Amount = the card's headline (Anton 2026-08-03): `toPayNow`, rendered with 0 decimals like
// FDebtCard, so the figure matches the card the user came from — a deliberate step away from the
// legacy avi-amount (ocr.amount). `toPayNow` is the shared rule from debtStage (it takes a single
// document, so no overview fan-out is needed to price one here). ADA-4445 withdrew the preliminary
// interest that used to be fetched and added on top.
const currency = computed(() => props.document.currency ?? props.ocr?.currency ?? 'SEK')
const amount = computed(() => toPayNow(props.document))
const amountSubtitle = computed(() => props.ocr?.creditor_name ?? props.document.creditor_name ?? '')
const creditorInformation = computed(() =>
  t('common.creditor_allows_respite_x_days', [props.document.creditor_name ?? '', props.params.days.max])
)

const date = ref<Date | null>(null)

const scheduleToExecute = computed(() => new Date(props.params.schedule_to_execute))
const minDate = computed(() => add(scheduleToExecute.value, { days: props.params.days.min }))
const maxDate = computed(() => add(scheduleToExecute.value, { days: props.params.days.max }))

// Own month/year navigation (Figma: a single centred "Month Year" label flanked by prev/next
// arrows — NOT Vuetify's docked month + year dropdowns, which are hidden via CSS below). The
// picker renders whatever month/year we bind; browsing is clamped to the min/max month span.
const displayMonth = ref(0)
const displayYear = ref(new Date(props.params.schedule_to_execute).getFullYear())
watch(
  minDate,
  (value) => {
    displayMonth.value = value.getMonth()
    displayYear.value = value.getFullYear()
  },
  { immediate: true }
)

const monthYearLabel = computed(() => {
  const label = formatMonthYear(new Date(displayYear.value, displayMonth.value, 1))
  return label.charAt(0).toUpperCase() + label.slice(1)
})

const displayMonthStart = computed(() => new Date(displayYear.value, displayMonth.value, 1))
const minMonthStart = computed(() => new Date(minDate.value.getFullYear(), minDate.value.getMonth(), 1))
const maxMonthStart = computed(() => new Date(maxDate.value.getFullYear(), maxDate.value.getMonth(), 1))
const canGoPrev = computed(() => displayMonthStart.value > minMonthStart.value)
const canGoNext = computed(() => displayMonthStart.value < maxMonthStart.value)

function prevMonth() {
  if (!canGoPrev.value) return
  if (displayMonth.value === 0) {
    displayMonth.value = 11
    displayYear.value -= 1
  } else {
    displayMonth.value -= 1
  }
}
function nextMonth() {
  if (!canGoNext.value) return
  if (displayMonth.value === 11) {
    displayMonth.value = 0
    displayYear.value += 1
  } else {
    displayMonth.value += 1
  }
}

function handleDate() {
  if (date.value == null) return
  const days = differenceInCalendarDays(date.value, scheduleToExecute.value)
  emit('confirm', { days, date: date.value })
}
</script>

<template>
  <section class="px-4 pb-2 d-flex flex-column">
    <div class="text-display-small font-weight-bold">{{ formatCurrency(amount, currency, 0) }}</div>
    <div class="text-title-medium mb-4">{{ amountSubtitle }}</div>

    <div class="text-headline-small font-weight-bold">{{ t('common.pick_new_due_date') }}</div>
    <div class="text-body-large text-medium-emphasis mb-4">{{ creditorInformation }}</div>

    <!-- Figma (ADA-4437, Anton 2026-08-03): interest keeps accruing while the payment is
         postponed. Blue (blue_main) icon + text. Belongs to the ADA-4434 interest story,
         pulled in here. -->
    <div class="respite-interest-notice d-flex align-center mb-4">
      <v-icon
        :icon="mdiInformation"
        color="blue_main"
        size="large"
        class="mr-4"
      />
      <span class="text-body-large">{{ t('common.respite_interest_continues_notice') }}</span>
    </div>

    <v-card
      variant="outlined"
      class="border"
    >
      <!-- Figma header: centred "Month Year" with a prev/next arrow on each side, month-only
           browsing. Vuetify's own controls are hidden via CSS; we drive the shown month with
           v-model:month/:year. -->
      <div class="respite-calendar-header d-flex align-center justify-space-between pt-2">
        <v-btn
          :disabled="!canGoPrev"
          variant="text"
          icon
          size="large"
          :aria-label="t('common.previous')"
          @click="prevMonth"
        >
          <v-icon
            :icon="mdiChevronLeft"
            size="36"
          />
        </v-btn>
        <span class="text-display-small mx-2">{{ monthYearLabel }}</span>
        <v-btn
          :disabled="!canGoNext"
          variant="text"
          icon
          size="large"
          :aria-label="t('common.next')"
          @click="nextMonth"
        >
          <v-icon
            :icon="mdiChevronRight"
            size="36"
          />
        </v-btn>
      </div>

      <v-divider class="my-2" />

      <v-date-picker
        v-model="date"
        v-model:month="displayMonth"
        v-model:year="displayYear"
        class="respite-date-picker"
        color="primary"
        :hide-header="true"
        :max="maxDate"
        :min="minDate"
        :show-adjacent-months="true"
        width="100%"
      />
    </v-card>
  </section>

  <section class="ma-4 d-flex">
    <v-spacer />
    <v-btn
      class="mr-4"
      variant="flat"
      @click="emit('cancel')"
    >
      {{ t('common.cancel') }}
    </v-btn>
    <v-btn
      :disabled="date == null"
      :loading="props.loading"
      color="primary"
      @click="handleDate"
    >
      {{ t('common.ok') }}
    </v-btn>
  </section>
</template>

<style scoped lang="sass">
.respite-interest-notice
  color: rgb(var(--v-theme-blue_main))

.respite-date-picker
  // Hide Vuetify's built-in month/year controls — our own centred header drives navigation.
  :deep(.v-date-picker-controls)
    display: none

  // NOTE (PR #895 review): an attempt to let the day cells scale fluidly with the dialog width
  // (aspect-ratio squares in the 1fr grid) collapsed the month to a single visible row — the
  // auto grid rows don't take the aspect-ratio-derived heights. Reverted; revisit with live
  // devtools if the wish comes back.

  // Figma day cells: rounded squares, not circles.
  :deep(.v-date-picker-month__day-btn)
    border-radius: 4px
    border: 1px solid rgba(var(--v-theme-grey), 0.1)

  // Selectable (in-range, enabled) days that are NOT the selected one get a light-grey square;
  // the selected day keeps Vuetify's color="primary" (black) fill, disabled/out-of-range days
  // stay plain grey text.
  :deep(.v-date-picker-month__day:not(.v-date-picker-month__day--selected) .v-date-picker-month__day-btn:not(:disabled))
    background-color: rgb(var(--v-theme-grey_light))
</style>
